/// <reference types="@types/google.maps" />
import { useState, useCallback, useEffect, useRef } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import { Input } from '@/components/ui/input';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// UK postcode regex - matches full UK postcodes
const UK_POSTCODE_REGEX = /\b([A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9][A-Z]{2})\b/i;

// Helper to extract full UK postcode from Google's address components or formatted address
function extractFullPostcode(
  addressComponents: google.maps.GeocoderAddressComponent[] | undefined,
  formattedAddress: string | undefined
): string | null {
  // First try to get from address_components
  if (addressComponents) {
    const postcodeComponent = addressComponents.find(
      comp => comp.types.includes('postal_code')
    );
    
    if (postcodeComponent) {
      const postcode = postcodeComponent.long_name || postcodeComponent.short_name;
      // Check if it's a full postcode (has both outward and inward parts)
      if (UK_POSTCODE_REGEX.test(postcode)) {
        return postcode.toUpperCase();
      }
    }
  }

  // Fallback: extract from formatted address using regex
  if (formattedAddress) {
    const match = formattedAddress.match(UK_POSTCODE_REGEX);
    if (match && match[1]) {
      // Normalize the postcode (add space if missing)
      let postcode = match[1].toUpperCase().replace(/\s+/g, '');
      // Insert space before the last 3 characters (inward code)
      if (postcode.length >= 5) {
        postcode = postcode.slice(0, -3) + ' ' + postcode.slice(-3);
      }
      return postcode;
    }
  }

  return null;
}

// UK center coordinates
const UK_CENTER = { lat: 52.4797, lng: -1.9026 };
const UK_BOUNDS = {
  north: 60.85,
  south: 49.84,
  west: -8.65,
  east: 1.77,
};

interface LatLngLiteral {
  lat: number;
  lng: number;
}

interface LocationPickerProps {
  address: string;
  postcode: string;
  onAddressChange: (address: string) => void;
  onPostcodeChange: (postcode: string) => void;
  onLocationChange: (lat: number, lng: number) => void;
  // Note: onWhat3WordsChange removed - what3words is now fetched in parent on Continue
}

export function LocationPicker({
  address,
  postcode,
  onAddressChange,
  onPostcodeChange,
  onLocationChange,
}: LocationPickerProps) {
  const [mapCenter, setMapCenter] = useState(UK_CENTER);
  const [mapZoom, setMapZoom] = useState(6);
  const [markerPosition, setMarkerPosition] = useState<LatLngLiteral | null>(null);
  const [what3words, setWhat3words] = useState<string>('');
  const [isGeocoding, setIsGeocoding] = useState(false);

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div className="space-y-4">
        {/* Address Autocomplete */}
        <PlaceAutocomplete
          address={address}
          onAddressChange={onAddressChange}
          onPostcodeChange={onPostcodeChange}
          onLocationSelect={(lat, lng) => {
            setMapCenter({ lat, lng });
            setMapZoom(17);
            setMarkerPosition({ lat, lng });
            onLocationChange(lat, lng);
          }}
        />

        {/* Postcode Input */}
        <div className="flex gap-3">
          <div className="flex-1">
            <Input
              label="Postcode"
              placeholder="e.g., B1 1AA"
              value={postcode}
              onChange={(e) => onPostcodeChange(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <GeocodeButton
              postcode={postcode}
              isLoading={isGeocoding}
              onGeocode={(lat, lng, formattedAddress) => {
                setMapCenter({ lat, lng });
                setMapZoom(17);
                setMarkerPosition({ lat, lng });
                onLocationChange(lat, lng);
                if (formattedAddress && !address) {
                  onAddressChange(formattedAddress);
                }
              }}
              onLoadingChange={setIsGeocoding}
            />
          </div>
        </div>

        {/* Map */}
        <div className="relative">
          <div className="rounded-xl overflow-hidden border border-surface-200 h-[350px]">
            <Map
              center={mapCenter}
              zoom={mapZoom}
              onCameraChanged={(e) => {
                const map = e.map;
                const center = map.getCenter();
                const zoom = map.getZoom();
                if (center && zoom !== undefined) {
                  setMapCenter({ lat: center.lat(), lng: center.lng() });
                  setMapZoom(zoom);
                }
              }}
              mapId="fixtyer-location-picker"
              gestureHandling="greedy"
              disableDefaultUI={false}
              zoomControl={true}
              streetViewControl={false}
              mapTypeControl={false}
              fullscreenControl={false}
              clickableIcons={false}
              restriction={{
                latLngBounds: UK_BOUNDS,
                strictBounds: false,
              }}
              onClick={(e) => {
                if (e.detail.latLng) {
                  const { lat, lng } = e.detail.latLng;
                  setMarkerPosition({ lat, lng });
                  onLocationChange(lat, lng);
                  // Get address from coordinates
                  reverseGeocode(lat, lng, onAddressChange, onPostcodeChange);
                  // Clear what3words - will be fetched on Continue
                  setWhat3words('');
                }
              }}
            >
              {markerPosition && (
                <DraggableMarker
                  position={markerPosition}
                  onDragEnd={(lat, lng) => {
                    setMarkerPosition({ lat, lng });
                    onLocationChange(lat, lng);
                    reverseGeocode(lat, lng, onAddressChange, onPostcodeChange);
                    // Clear what3words - will be fetched on Continue
                    setWhat3words('');
                  }}
                />
              )}
            </Map>
          </div>

          {/* Map instructions */}
          <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur rounded-lg px-3 py-2 shadow-sm border border-surface-200">
            <p className="text-xs text-surface-700">
              {markerPosition 
                ? '📍 Drag the pin to adjust the exact location' 
                : '👆 Click on the map to place a pin, or search for an address above'
              }
            </p>
          </div>
        </div>

        {/* What3Words display */}
        {what3words && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
            <div className="w-8 h-8 bg-red-500 rounded flex items-center justify-center">
              <span className="text-white text-xs font-bold">///</span>
            </div>
            <div>
              <p className="text-xs text-red-600 font-medium">what3words</p>
              <p className="text-sm font-mono text-red-700">{what3words}</p>
            </div>
          </div>
        )}

        {/* Location info */}
        {markerPosition && (
          <div className="p-3 bg-brand-50 border border-brand-100 rounded-xl">
            <p className="text-xs text-brand-600 font-medium mb-1">📍 Selected Location</p>
            <p className="text-sm text-brand-800 font-mono">
              {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
            </p>
          </div>
        )}
      </div>
    </APIProvider>
  );
}

// Place Autocomplete Component
function PlaceAutocomplete({
  address,
  onAddressChange,
  onPostcodeChange,
  onLocationSelect,
}: {
  address: string;
  onAddressChange: (address: string) => void;
  onPostcodeChange: (postcode: string) => void;
  onLocationSelect: (lat: number, lng: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary('places');
  const geocoding = useMapsLibrary('geocoding');
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [localAddress, setLocalAddress] = useState(address);

  // Update local address when prop changes
  useEffect(() => {
    setLocalAddress(address);
  }, [address]);

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const autocompleteInstance = new places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'gb' },
      fields: ['formatted_address', 'geometry', 'address_components'],
      types: ['address'],
    });

    autocompleteInstance.addListener('place_changed', () => {
      const place = autocompleteInstance.getPlace();
      if (place.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        onLocationSelect(lat, lng);
        const formattedAddress = place.formatted_address || '';
        onAddressChange(formattedAddress);
        setLocalAddress(formattedAddress);

        // Extract full postcode from address components or formatted address
        const fullPostcode = extractFullPostcode(place.address_components, formattedAddress);
        if (fullPostcode) {
          onPostcodeChange(fullPostcode);
        }
      }
    });

    autocompleteRef.current = autocompleteInstance;

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [places, onAddressChange, onPostcodeChange, onLocationSelect]);

  // Manual search function for when user doesn't use autocomplete
  const handleManualSearch = useCallback(async () => {
    if (!geocoding || !localAddress.trim()) return;

    setIsSearching(true);
    const geocoder = new geocoding.Geocoder();

    try {
      const result = await geocoder.geocode({
        address: localAddress,
        componentRestrictions: { country: 'GB' },
      });

      if (result.results[0]?.geometry?.location) {
        const location = result.results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        
        onLocationSelect(lat, lng);
        
        const formattedAddress = result.results[0].formatted_address;
        if (formattedAddress) {
          onAddressChange(formattedAddress);
          setLocalAddress(formattedAddress);
        }

        // Extract full postcode
        const fullPostcode = extractFullPostcode(
          result.results[0].address_components,
          formattedAddress
        );
        if (fullPostcode) {
          onPostcodeChange(fullPostcode);
        }
      }
    } catch (error) {
      console.error('Address search failed:', error);
    } finally {
      setIsSearching(false);
    }
  }, [geocoding, localAddress, onLocationSelect, onAddressChange, onPostcodeChange]);

  // Handle Enter key to search
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleManualSearch();
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-brand-950 mb-1.5">
        Search for address
      </label>
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={localAddress}
          placeholder="Start typing an address..."
          className="flex-1 px-4 py-2.5 rounded-xl border border-surface-300 bg-white text-brand-950 placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
          onChange={(e) => {
            setLocalAddress(e.target.value);
            onAddressChange(e.target.value);
          }}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          onClick={handleManualSearch}
          disabled={!localAddress.trim() || isSearching}
          className="px-4 py-2.5 bg-brand-600 text-white font-medium rounded-xl hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
        >
          {isSearching ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </span>
          ) : (
            '🔍 Search'
          )}
        </button>
      </div>
      <p className="mt-1 text-xs text-surface-600">
        💡 Select from suggestions, press Enter, or click Search to find on map
      </p>
    </div>
  );
}

// Draggable Marker Component
function DraggableMarker({
  position,
  onDragEnd,
}: {
  position: LatLngLiteral;
  onDragEnd: (lat: number, lng: number) => void;
}) {
  const [isDragging, setIsDragging] = useState(false);

  return (
    <AdvancedMarker
      position={position}
      draggable
      onDragStart={() => setIsDragging(true)}
      onDragEnd={(e) => {
        setIsDragging(false);
        if (e.latLng) {
          onDragEnd(e.latLng.lat(), e.latLng.lng());
        }
      }}
    >
      <div
        className={`transition-transform ${isDragging ? 'scale-125' : 'scale-100'}`}
      >
        <svg width="40" height="48" viewBox="0 0 40 48" fill="none">
          <path
            d="M20 0C8.954 0 0 8.954 0 20c0 14.4 20 28 20 28s20-13.6 20-28C40 8.954 31.046 0 20 0z"
            fill="#DC2626"
          />
          <circle cx="20" cy="18" r="8" fill="white" />
          <circle cx="20" cy="18" r="4" fill="#DC2626" />
          <ellipse
            cx="20"
            cy="46"
            rx="6"
            ry="2"
            fill="rgba(0,0,0,0.2)"
          />
        </svg>
      </div>
    </AdvancedMarker>
  );
}

// Geocode Button Component
function GeocodeButton({
  postcode,
  isLoading,
  onGeocode,
  onLoadingChange,
}: {
  postcode: string;
  isLoading: boolean;
  onGeocode: (lat: number, lng: number, address?: string) => void;
  onLoadingChange: (loading: boolean) => void;
}) {
  const geocoding = useMapsLibrary('geocoding');

  const handleGeocode = useCallback(async () => {
    if (!geocoding || !postcode) return;

    onLoadingChange(true);
    const geocoder = new geocoding.Geocoder();

    try {
      const result = await geocoder.geocode({
        address: postcode,
        componentRestrictions: { country: 'GB' },
      });

      if (result.results[0]?.geometry?.location) {
        const location = result.results[0].geometry.location;
        onGeocode(
          location.lat(),
          location.lng(),
          result.results[0].formatted_address
        );
      }
    } catch (error) {
      console.error('Geocoding failed:', error);
    } finally {
      onLoadingChange(false);
    }
  }, [geocoding, postcode, onGeocode, onLoadingChange]);

  return (
    <button
      type="button"
      onClick={handleGeocode}
      disabled={!postcode || isLoading}
      className="px-4 py-2.5 bg-brand-600 text-white font-medium rounded-xl hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
    >
      {isLoading ? (
        <span className="flex items-center gap-2">
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Finding...
        </span>
      ) : (
        '📍 Find on map'
      )}
    </button>
  );
}

// Reverse Geocode helper
async function reverseGeocode(
  lat: number,
  lng: number,
  onAddressChange: (address: string) => void,
  onPostcodeChange: (postcode: string) => void
) {
  try {
    const geocoder = new google.maps.Geocoder();
    const result = await geocoder.geocode({ location: { lat, lng } });

    if (result.results[0]) {
      const address = result.results[0].formatted_address;
      onAddressChange(address);

      // Extract full postcode
      const fullPostcode = extractFullPostcode(
        result.results[0].address_components,
        address
      );
      if (fullPostcode) {
        onPostcodeChange(fullPostcode);
      }
    }
  } catch (error) {
    console.error('Reverse geocoding failed:', error);
  }
}

// Note: What3Words is now fetched in the parent component (new.tsx) 
// only when the user clicks Continue, to minimize API usage
